import { useEffect, useRef, useState } from "react";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  ShopOutlined,
  RocketOutlined,
  ArrowRightOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./Home.css";

function useInView(threshold = 0.08) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold }
    );

    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, vis];
}

/* ================================================================
   HERO
================================================================ */
function Hero() {
  const [on, setOn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setOn(true), 80);
    return () => clearTimeout(t);
  }, []);

  const a = (d = 0) => ({
    opacity: on ? 1 : 0,
    transform: on ? "translateY(0)" : "translateY(18px)",
    transition: `all .65s cubic-bezier(.16,1,.3,1) ${d}s`,
  });

  const rows = [
    {
      img: "/images/tweek1.jpg",
      name: "Premium Retail Space",
      sub: "Commercial · Available to lease",
      tag: "Space",
    },
    {
      img: "/images/tweek2.jpg",
      name: "Food & Retail Stall",
      sub: "Stall · Mall & event locations",
      tag: "Stall",
    },
    {
      img: "/images/tweek3.jpg",
      name: "Verified Franchise Opportunity",
      sub: "Franchise · Multiple cities",
      tag: "Franchise",
    },
    {
      img: "/images/tweek4.jpg",
      name: "Established Business for Sale",
      sub: "Business · Direct owner contact",
      tag: "Business",
    },
    {
      img: "/images/tweek5.webp",
      name: "Highway Billboard Space Available",
      sub: "Hoarding · Heavy vehicle traffic",
      tag: "Hoarding",
    },
  ];

  return (
    <section className="hero">
      <div className="hero__bg" />
      <div className="hero__glow-2" />

      <div className="hero__inner">
        <div>
          <h1 className="hero__h1" style={a(0.13)}>
            Find commercial
            <span className="hero__h1-line2">opportunities faster</span>
          </h1>

          <p className="hero__sub" style={a(0.2)}>
            Browse verified listings for spaces, stalls, businesses, Hoardings and
            franchises. Or post your own ad — direct, free, zero commission.
          </p>

          <div className="hero__cta" style={a(0.26)}>
            <Link to="/explore">
              <button
                className="btn btn-dark"
                style={{ background: "#fff", color: "#0f172a" }}
              >
                <SearchOutlined /> Explore Listings
              </button>
            </Link>

            <Link
              to={localStorage.getItem("token") ? "/add" : "/login"}
              className="btn-ghost"
            >
              Post a free ad <ArrowRightOutlined style={{ fontSize: 12 }} />
            </Link>
          </div>

          <div className="hero__proof" style={a(0.32)}>
            {[
              { val: "5", lbl: "Categories" },
              { val: "Free", lbl: "To advertise" },
              { val: "Direct", lbl: "Owner contact" },
              { val: "0%", lbl: "Commission" },
            ].map((p) => (
              <div key={p.lbl} className="hero__proof-item">
                <span className="hero__proof-val">{p.val}</span>
                <span className="hero__proof-lbl">{p.lbl}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={a(0.2)}>
          <div className="hero__card">
            <div className="hero__card-bar">
              <span className="hero__card-title">Active Listings</span>
              <span className="hero__card-live">Live now</span>
            </div>

            <div className="hero__card-body">
              {rows.map((r) => (
                <div key={r.name} className="ui-row">
                  <div className="ui-row__ico">
                    <img src={r.img} alt={r.name} />
                  </div>

                  <div className="ui-row__body">
                    <div className="ui-row__name">{r.name}</div>
                    <div className="ui-row__sub">{r.sub}</div>
                  </div>

                  <span className="ui-row__tag">{r.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hero__fade" />
    </section>
  );
}

/* ================================================================
   TRUST
================================================================ */
function Trust() {
  return (
    <div className="trust">
      <p className="trust__text">
        Trusted by entrepreneurs, property owners, and investors across India
      </p>
    </div>
  );
}

/* ================================================================
   CATEGORIES
================================================================ */
function Categories() {
  const [ref, vis] = useInView();

  const cats = [
    {
      icon: <ApartmentOutlined />,
      name: "Commercial Spaces",
      desc: "Offices, showrooms, and retail units for lease or sale.",
    },
    {
      icon: <AppstoreOutlined />,
      name: "Stalls",
      desc: "Market and mall stalls in high-footfall commercial areas.",
    },
    {
      icon: <ShopOutlined />,
      name: "Businesses",
      desc: "Established businesses for sale, listed by owners directly.",
    },
    {
      icon: <RocketOutlined />,
      name: "Franchises",
      desc: "Verified franchise opportunities from growing brands.",
    },
  ];

  return (
    <section className="cats" ref={ref}>
      <div className="cats__inner">
        <div className="cats__head">
          <div className={`rv ${vis ? "in" : ""}`}>
            <span className="sec-eyebrow">Categories</span>
            <h2 className="sec-title">What you can find on Draptor</h2>
          </div>

          <Link to="/explore" className={`sec-link rv d1 ${vis ? "in" : ""}`}>
            View all <ArrowRightOutlined style={{ fontSize: 11 }} />
          </Link>
        </div>

        <div className="cats__grid">
          {cats.map((c, i) => (
            <Link
              key={c.name}
              to=""
              className={`cat-card rv d${i + 1} ${vis ? "in" : ""}`}
            >
              <div className="cat-ico">{c.icon}</div>
              <div className="cat-name">{c.name}</div>
              <p className="cat-desc">{c.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   FEATURES
================================================================ */
function Features() {
  const [ref, vis] = useInView();

  const feats = [
    {
      title: "Verified Listings",
      text: "Every listing is posted directly by the owner or authorised representative. No fake ads.",
    },
    {
      title: "Direct Owner Contact",
      text: "Connect with owners instantly. No agents, no middlemen, no delayed responses.",
    },
    {
      title: "Zero Commission",
      text: "Draptor charges no commission, ever. List or enquire completely free of charge.",
    },
  ];

  return (
    <section className="features" ref={ref}>
      <div className="features__inner">
        <div className={`features__head rv ${vis ? "in" : ""}`}>
          <span className="sec-eyebrow">Why Draptor</span>
          <h2 className="sec-title">Built on three principles</h2>
        </div>

        <div className="features__grid">
          {feats.map((f, i) => (
            <div key={f.title} className={`feat rv d${i + 1} ${vis ? "in" : ""}`}>
              <div className="feat__check">✓</div>
              <div className="feat__title">{f.title}</div>
              <p className="feat__text">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   HOW IT WORKS
================================================================ */
function HowItWorks() {
  const [ref, vis] = useInView();

  const steps = [
    {
      n: "1",
      title: "Browse or search",
      text: "Explore listings by category and location to find the right opportunity.",
    },
    {
      n: "2",
      title: "Contact the owner",
      text: "Access direct contact details and connect without any intermediary.",
    },
    {
      n: "3",
      title: "Close your deal",
      text: "Finalise on your terms. No platform fees, no commission involved.",
    },
  ];

  return (
    <section className="how" id="how-it-works" ref={ref}>
      <div className="how__inner">
        <div className={`how__head rv ${vis ? "in" : ""}`}>
          <span className="sec-eyebrow">How it works</span>
          <h2 className="sec-title">Simple from start to finish</h2>
        </div>

        <div className="how__steps">
          {steps.map((s, i) => (
            <div key={s.n} className={`how-step rv d${i + 1} ${vis ? "in" : ""}`}>
              <div className="how-step__num">{s.n}</div>
              <div className="how-step__title">{s.title}</div>
              <p className="how-step__text">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   CTA
================================================================ */
function CTA() {
  const [ref, vis] = useInView(0.1);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      const stored = JSON.parse(localStorage.getItem("user"));
      setUser(stored);
    };

    loadUser();

    const handleUpdate = () => loadUser();
    window.addEventListener("authChange", handleUpdate);

    return () => {
      window.removeEventListener("authChange", handleUpdate);
    };
  }, []);

  return (
    <section className="cta-sec">
      <div className="cta-inner">
        <div ref={ref} className={`cta-box rv ${vis ? "in" : ""}`}>
          <div>
            <span className="cta-label">Get started — it's free</span>

            <h2 className="cta-h2">
              Discover or List
              <br />
              Real Business Opportunities
            </h2>

            <p className="cta-sub">
              Find spaces, franchises, stalls, Advertisements and businesses — or
              list yours in minutes.
            </p>
          </div>

          <div className="cta-actions">
            <Link to={user ? "/explore" : "/register"}>
              <button className="btn btn-cta-inv">
                <UserOutlined />
                {user ? "Explore Opportunities" : "Get Started for Free"}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   FOOTER
================================================================ */
function Footer() {
  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__top">
          <div>
            <div className="footer__brand">
              <img
                src="/images/brandlogo.png"
                alt="Draptor"
                className="footer__logo"
              />
              <span className="footer__name">DRAPTOR</span>
            </div>

            <p className="footer__desc">
              India's focused marketplace for commercial spaces, stalls,
              businesses, Advertisements and franchise opportunities. Direct.
              Free. No commission.
            </p>
          </div>

          <div>
            <div className="footer__col-head">Explore</div>
            <Link to="/explore" className="footer__link">All Listings</Link>
            <Link to="/commercial-spaces" className="footer__link">Commercial Spaces</Link>
            <Link to="/stalls" className="footer__link">Stalls</Link>
            <Link to="/franchises" className="footer__link">Franchises</Link>
            <Link to="/businesses" className="footer__link">Businesses</Link>
            <Link to="/ads" className="footer__link">Ads Business</Link>
          </div>

          <div>
            <div className="footer__col-head">Platform</div>
            <a href="#how-it-works" className="footer__link">How it works</a>
            <Link
              to={localStorage.getItem("token") ? "/add" : "/login"}
              className="footer__link"
            >
              Post an Ad
            </Link>
          </div>

          <div>
            <div className="footer__col-head">Help & Support</div>
            <Link
              to={localStorage.getItem("token") ? "/contact" : "/login"}
              className="footer__link"
            >
              Contact us
            </Link>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__copy">© {new Date().getFullYear()} Draptor. All rights reserved.</p>
          <div className="footer__legal">
            <a href="/">Privacy Policy</a>
            <a href="/">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ================================================================
   PAGE
================================================================ */
export default function Home() {
  return (
    <div className="hp">
      <Hero />
      <Trust />
      <Categories />
      <Features />
      <HowItWorks />
      <CTA />
      <Footer />
    </div>
  );
}